import { getUrgencyColor, getUrgencyIcon } from 'src/graphql/entities/conversation-urgency';
import { parseJsonSafely } from 'src/utils/objects';
import type { TriageActionAccent, TriageActionDisplay } from './conversation-triage-run.types';

interface ToolPresentation {
  icon: string;
  accent: TriageActionAccent;
  color?: string;
}

const CHANGE_URGENCY_TOOL = 'change_urgency';

const TOOL_PRESENTATION: Record<string, ToolPresentation> = {
  change_urgency: { icon: 'priority_high', accent: 'urgency' },
  change_status: { icon: 'play_circle', accent: 'status' },
  change_conversation_type: { icon: 'confirmation_number', accent: 'type' },
  change_conversation_assignee_group: { icon: 'groups', accent: 'group' },
  change_requester: { icon: 'person', accent: 'assignee' },
  snooze: { icon: 'snooze', accent: 'default' },
};

const DEFAULT_PRESENTATION: ToolPresentation = { icon: 'auto_awesome', accent: 'default' };

const DEFAULT_ACTION_LABEL = 'Triage action';

// Most tools target a single facet of the conversation (one urgency, one
// status), so all of a tool's actions in a run are retries of the same logical
// change and collapse into one chip. A few tools legitimately fire once per
// target — `add_label` attaches one label per call — so their actions must be
// keyed by that target to render as distinct chips while still merging genuine
// retries of the same target. Maps a tool name to the payload arg holding its
// per-instance target id.
const MULTI_INSTANCE_TOOL_TARGET_ARG: Record<string, string> = {
  add_label: 'labelId',
};

interface ParsedPayload {
  toolName?: string;
  args?: Record<string, unknown>;
}

export function getActionToolName(payload?: string | null): string | null {
  return parseJsonSafely<ParsedPayload>(payload ?? '')?.toolName ?? null;
}

// Identity used to group action rows into a single chip. Tools collapse by name
// (retries merge), except multi-instance tools which also key on their target
// id so distinct targets — e.g. each label of `add_label` — stay separate.
export function getActionGroupKey(payload?: string | null): string | null {
  const parsed = parseJsonSafely<ParsedPayload>(payload ?? '');
  const toolName = parsed?.toolName ?? null;
  if (!toolName) {
    return null;
  }

  const targetArg = MULTI_INSTANCE_TOOL_TARGET_ARG[toolName];
  if (targetArg) {
    const targetValue = parsed?.args?.[targetArg];
    if (typeof targetValue === 'string' && targetValue) {
      return `${toolName}:${targetValue}`;
    }
  }

  return toolName;
}

export function getActionReason(payload?: string | null): string | null {
  const reason = parseJsonSafely<ParsedPayload>(payload ?? '')?.args?.reason;
  return typeof reason === 'string' && reason.trim() ? reason.trim() : null;
}

export function getActionPresentation(payload?: string | null): ToolPresentation {
  const parsed = parseJsonSafely<ParsedPayload>(payload ?? '');
  const toolName = parsed?.toolName ?? null;
  const base = (toolName && TOOL_PRESENTATION[toolName]) || DEFAULT_PRESENTATION;

  if (toolName === CHANGE_URGENCY_TOOL) {
    const urgencyId = parsed?.args?.conversationUrgencyId;
    if (typeof urgencyId === 'string' && urgencyId) {
      return {
        icon: getUrgencyIcon(urgencyId),
        accent: 'urgency',
        color: getUrgencyColor(urgencyId),
      };
    }
  }

  return base;
}

function humanizeToolName(toolName: string): string {
  const words = toolName.replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : DEFAULT_ACTION_LABEL;
}

// Visible label comes from the backend display snapshot; when it is missing we
// fall back to a humanized tool name so the chip never renders empty.
export function getActionLabel(
  display?: string | null,
  payload?: string | null,
): { label: string; hasLabel: boolean } {
  const snapshotLabel = parseJsonSafely<TriageActionDisplay>(display ?? '')?.label?.trim();
  if (snapshotLabel) {
    return { label: snapshotLabel, hasLabel: true };
  }

  const toolName = getActionToolName(payload);
  return {
    label: toolName ? humanizeToolName(toolName) : DEFAULT_ACTION_LABEL,
    hasLabel: false,
  };
}
